import * as React from "react";
import { Autocomplete, Stack, TextField, AlertColor, Box } from "@mui/material";
import { DataGridPremium, GridColDef } from "@mui/x-data-grid-premium";
import CclButton from "../../../../components/common/cclButtons/cclButton";
import CclDrawer from "../../../../components/common/cclDrawer/cclDrawer";
import CclDrawerActionBar from "../../../../components/common/cclDrawer/cclDrawerActionBar";
import { cclTokenedPayrollApi } from "../../../../services/cclTokenedPayrollApi";
import { CreatePaymentDetail, GlCode } from "../../../../services/types/payrollEventTypes";
import { useGetActiveResourcesQuery } from "../../../../services/cclTokenedEnterpriseCoachingApi";
import { PayRate } from "../../../../services/types/payrollEventTypes";
import { ResourceDocument } from "../../../../services/types/search.service.types";
import useLogAccessEvent from "../../../../hooks/useLogAccessEvent";
import { AccessEventOFCDetails, AccessEventOFCReducedDetails } from "../../../../services/types/accessEventTypes";
import { UserClaimsService } from "../../../../services/currentUserService/currentUserService";
import CclGenericAccordian from "../../../../components/common/cclGenericAccordian";

export interface TrainerEntryFormValues {
    paymentId?: number;
    eventTrainer?: number;
    trainerName: string;
    glCode: string;
    rateCode: string;
    units: string;
    hours: string;
    fee: string;
}

export interface ModifyTrainerEntryDrawerProps {
    sessionId: string;
    projectCode: string;
    eventKey: number;
    newEntry: boolean;
    trainer?: TrainerEntryFormValues;
    open: boolean;
    onClose: () => void;
    onSave?: () => void;
    onShowSnackbar: (message: string, severity: AlertColor) => void;
}

type TrainerOption = {
    label: string;
    imKey: number;
};

type RateCodeRow = {
    id: string;
    rateCode: string;
    rateDescription: string;
    unit: string;
    currentFee: string;
};

const ModifyTrainerEntryDrawer: React.FC<ModifyTrainerEntryDrawerProps> = (props) => {
    const [trainerName, setTrainerName] = React.useState<string>("");
    const [eventTrainer, setEventTrainer] = React.useState<number | null>(null);
    const [glCode, setGlCode] = React.useState<string>("");
    const [glCodeInput, setGlCodeInput] = React.useState<string>("");
    const [rateCode, setRateCode] = React.useState<string>("");
    const [rateCodeInput, setRateCodeInput] = React.useState<string>("");
    const [units, setUnits] = React.useState<string>("");
    const [hours, setHours] = React.useState<string>("");
    const [fee, setFee] = React.useState<string>("");
    const [addPaymentDetail, { isLoading: isSaving }] = cclTokenedPayrollApi.useAddPaymentDetailMutation();
    const [editPaymentDetail, { isLoading: isEditingSaving }] = cclTokenedPayrollApi.useEditPaymentDetailMutation();
    const { data: glData } = cclTokenedPayrollApi.useGetGlDataQuery();
    const { data: payRatesData, isLoading: isPayRatesLoading } = cclTokenedPayrollApi.useGetPayRatesQuery();
    const { data: activeResources = [], isLoading: isActiveResourcesLoading } =
        useGetActiveResourcesQuery();
    const { logEvent } = useLogAccessEvent();
    const claimservice = new UserClaimsService();

    const isEditingTrainer = props.trainer != null;

    React.useEffect(() => {
        if (!props.open) {
            return;
        }

        if (props.trainer) {
            setTrainerName(props.trainer.trainerName ?? "");
            setEventTrainer(props.trainer.eventTrainer ?? null);
            setGlCode(props.trainer.glCode ?? "");
            setGlCodeInput(props.trainer.glCode ?? "");
            setRateCode(props.trainer.rateCode ?? "");
            setRateCodeInput(props.trainer.rateCode ?? "");
            setUnits(props.trainer.units ?? "");
            setHours(props.trainer.hours ?? "");
            setFee(props.trainer.fee ?? "");
            return;
        }

        setTrainerName("");
        setEventTrainer(null);
        setGlCode("");
        setGlCodeInput("");
        setRateCode("");
        setRateCodeInput("");
        setUnits("");
        setHours("");
        setFee("");
    }, [props.open, props.trainer]);

    const formatGlCodeOption = React.useCallback(
        (option: { glCode?: string }) => String(option.glCode ?? ""),
        [],
    );

    const formatRateCodeOption = React.useCallback(
        (option: PayRate) => String(option.rateCode ?? ""),
        [],
    );

    const glCodeOptions = React.useMemo<GlCode[]>(
        () =>
            [...(glData?.glCodes ?? [])]
                .filter((glCode) => glCode.active)
                .sort((a, b) =>
                    String(a.glCode ?? "").localeCompare(String(b.glCode ?? ""))
                ),
        [glData]
    );

    const normalizeTrainerName = React.useCallback(
        (value: string) => value.trim().replace(/\s+/g, " ").toLowerCase(),
        [],
    );

    const trainerNameOptions = React.useMemo<TrainerOption[]>(() => {
        const toDisplayName = (resource: ResourceDocument): TrainerOption | null => {
            const label = [resource.firstname, resource.lastname]
                .filter((part) => String(part ?? "").trim() !== "")
                .map((part) => String(part).trim())
                .join(" ");
            const normalizedLabel = normalizeTrainerName(label);

            if (normalizedLabel === "" || normalizedLabel.includes("deleted")) {
                return null;
            }

            return {
                label,
                imKey: Number(resource.imkey),
            };
        };

        const options: TrainerOption[] = activeResources
            .filter((resource: ResourceDocument) => String(resource.status ?? "").trim().toLowerCase() === "active")
            .map((resource: ResourceDocument) => toDisplayName(resource))
            .filter((option: TrainerOption | null): option is TrainerOption => option != null)
            .filter((option: TrainerOption) => Number.isFinite(option.imKey));

        const byTrainerName = new Map<string, TrainerOption>();
        options.forEach((option) => {
            const normalizedLabel = normalizeTrainerName(option.label);
            if (!byTrainerName.has(normalizedLabel)) {
                byTrainerName.set(normalizedLabel, option);
            }
        });
    
        return [...byTrainerName.values()].sort((a: TrainerOption, b: TrainerOption) => a.label.localeCompare(b.label));
    }, [activeResources, normalizeTrainerName]);

    const rateCodeOptions = React.useMemo<PayRate[]>(() => {
        const payRatesResponse = (payRatesData ?? []) as PayRate[] | { payRates?: PayRate[]; data?: PayRate[] };

        const rows = Array.isArray(payRatesResponse)
            ? payRatesResponse
            : Array.isArray(payRatesResponse.payRates)
              ? payRatesResponse.payRates
              : Array.isArray(payRatesResponse.data)
                ? payRatesResponse.data
                : [];

        const isActive = (value: unknown): boolean => {
            if (typeof value === "boolean") {
                return value;
            }

            if (typeof value === "string") {
                return value.trim().toLowerCase() === "true";
            }

            if (typeof value === "number") {
                return value === 1;
            }

            return false;
        };

        return [...rows]
            .filter((rate) => isActive((rate as PayRate & { active?: unknown }).active))
            .sort((a, b) => String(a.rateCode ?? "").localeCompare(String(b.rateCode ?? "")));
    }, [payRatesData]);

    const rateCodeColumns = React.useMemo<GridColDef[]>(
        () => [
            {
                field: "rateCode",
                headerName: "Rate Code",
                minWidth: 140,
                flex: 0.35,
            },
            {
                field: "rateDescription",
                headerName: "Description",
                minWidth: 160,
                flex: .5,
            },
            {
                field: "unit",
                headerName: "Unit",
                minWidth: 120,
                flex: 0.3,
            },
            {
                field: "currentFee",
                headerName: "Current Fee",
                minWidth: 140,
                flex: 0.35,
            },
        ],
        [],
    );

    const rateCodeRows = React.useMemo<RateCodeRow[]>(() => {
        return rateCodeOptions.map((rate, index) => ({
            id: String(rate.rateCode ?? `rate-code-${index}`),
            rateCode: String(rate.rateCode ?? ""),
            rateDescription: String(rate.rateDesc ?? ""),
            unit: String(rate.unit ?? ""),
            currentFee: rate.currentFee != null ? String(rate.currentFee) : "",
        }));
    }, [rateCodeOptions]);

    const selectedTrainerName = React.useMemo<TrainerOption | null>(() => {
        if (eventTrainer == null && !trainerName) {
            return null;
        }

        return (
            trainerNameOptions.find((option) => option.imKey === eventTrainer) ??
            trainerNameOptions.find((option) => normalizeTrainerName(option.label) === normalizeTrainerName(trainerName)) ??
            null
        );
    }, [eventTrainer, trainerName, trainerNameOptions, normalizeTrainerName]);

    const resolvedEventTrainer = React.useMemo<number | null>(() => {
        if (eventTrainer != null) {
            return eventTrainer;
        }

        const normalizedTrainerName = normalizeTrainerName(trainerName);
        if (normalizedTrainerName === "") {
            return null;
        }

        const exactMatch = trainerNameOptions.find(
            (option) => normalizeTrainerName(option.label) === normalizedTrainerName,
        );
        if (exactMatch) {
            return exactMatch.imKey;
        }

        const startsWithMatches = trainerNameOptions.filter((option) =>
            normalizeTrainerName(option.label).startsWith(normalizedTrainerName),
        );

        if (startsWithMatches.length === 1) {
            return startsWithMatches[0].imKey;
        }

        const containsMatches = trainerNameOptions.filter((option) =>
            normalizeTrainerName(option.label).includes(normalizedTrainerName),
        );

        if (containsMatches.length === 1) {
            return containsMatches[0].imKey;
        }

        return null;
    }, [eventTrainer, trainerName, trainerNameOptions, normalizeTrainerName]);

    const selectedGlCode = React.useMemo(() => {
        if (!glCode) {
            return null;
        }

        const normalizedGlCode = String(glCode ?? "").trim().toLowerCase();
        return (
            glCodeOptions.find(
                (option) => String(option.glCode ?? "").trim().toLowerCase() === normalizedGlCode,
            ) ?? null
        );
    }, [glCode, glCodeOptions]);

    const selectedRateCode = React.useMemo(() => {
        if (!rateCode) {
            return null;
        }

        return rateCodeOptions.find((option) => option.rateCode === rateCode) ?? null;
    }, [rateCode, rateCodeOptions]);

    const handleRateCodeChange = (value: PayRate | null) => {
        setRateCode(value?.rateCode ?? "");

        if (!value) {
            setFee("");
            return;
        }

        setFee(value.currentFee != null ? String(value.currentFee) : "");
    };

    const parseNumber = (value: string): number | null => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const canSave =
        resolvedEventTrainer != null &&
        props.eventKey > 0 &&
        glCode !== "" &&
        rateCode.trim() !== "" &&
        parseNumber(units) != null &&
        parseNumber(hours) != null &&
        parseNumber(fee) != null;

    const handleSave = async () => {
        if (!canSave || resolvedEventTrainer == null) {
            return;
        }

        const selectedTrainerImKey = resolvedEventTrainer;
        const parsedHours = parseNumber(hours);
        const parsedFee = parseNumber(fee);

        if (
            parsedHours == null ||
            parsedFee == null
        ) {
            return;
        }

        const parsedUnits = parseNumber(units);

        if (parsedUnits == null) {
            return;
        }

        const payload: CreatePaymentDetail = {
            paymentId: props.trainer?.paymentId,
            imKey: selectedTrainerImKey,
            eventTrainer: selectedTrainerImKey,
            rateCode,
            fee: parsedFee,
            glCode,
            eventKey: props.eventKey,
            units: String(parsedUnits),
            hours: parsedHours,
        };

        if (isEditingTrainer) {
            if (payload.paymentId == null) {
                console.error("Cannot edit payment detail without paymentId");
                return;
            }

            editPaymentDetail(payload)
                .unwrap()
                .then(() => {
                    const evtData: AccessEventOFCReducedDetails = {
                        sessionID: props.sessionId,
                        projectCode: props.projectCode,
                    };
                    logEvent("PaymentDetailEdited", evtData);

                    props.onShowSnackbar("Faculty Entry Edited", "success");
                    props.onSave?.();
                    props.onClose();
                })
                .catch((error) => {
                    console.error("Failed to update faculty entry:", error);

                    props.onShowSnackbar("Failed to update Faculty Entry", "error");
                });
            return;
        }

        addPaymentDetail(payload)
            .unwrap()
            .then(() => {
                const evtData: AccessEventOFCReducedDetails = {
                    sessionID: props.sessionId,
                    projectCode: props.projectCode,
                };
                logEvent("PaymentDetailAdded", evtData);

                props.onShowSnackbar("Faculty Entry Added", "success");
                props.onSave?.();
                props.onClose();
            })
            .catch((error) => {
                console.error("Failed to save faculty entry:", error);

                props.onShowSnackbar("Failed to add Faculty Entry", "error");
            });
    };

    const lockFeeField = rateCode.trim() !== "" && parseNumber(fee) != null;

    return (
        <CclDrawer 
            title={isEditingTrainer || !props.newEntry ? "Edit Faculty Entry" : "Add Faculty Entry"} 
            open={props.open} 
            onClose={props.onClose }
        >
            <Stack spacing={2}>
                <Autocomplete
                    id="trainer-entry-name"
                    options={trainerNameOptions}
                    loading={isActiveResourcesLoading}
                    value={selectedTrainerName}
                    inputValue={trainerName}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.imKey === value.imKey}
                    filterOptions={(options, state) => {
                        const normalizedInput = normalizeTrainerName(state.inputValue);
                        if (normalizedInput === "") {
                            return options;
                        }
                        
                        return options.filter((option) =>
                            normalizeTrainerName(option.label).includes(normalizedInput)
                        );
                    }}
                    onInputChange={(_, value: string) => {
                        setTrainerName(value);

                        const normalizedValue = normalizeTrainerName(value);
                        const matchedOption = trainerNameOptions.find(
                            (option) => normalizeTrainerName(option.label) === normalizedValue,
                        );
                        setEventTrainer(matchedOption?.imKey ?? null);
                    }}
                    onChange={(_, value: TrainerOption | null) => {
                        setTrainerName(value?.label ?? "");
                        setEventTrainer(value?.imKey ?? null);
                    }}
                    renderInput={(params) => (
                        <TextField {...params} label="Faculty" size="small" fullWidth />
                    )}
                />
                <Autocomplete
                    id="trainer-entry-gl-code"
                    options={glCodeOptions}
                    loading={!glData}
                    value={selectedGlCode}
                    inputValue={glCodeInput}
                    getOptionLabel={(option) => formatGlCodeOption(option)}
                    isOptionEqualToValue={(option, value) => option.glCode === value.glCode}
                    onInputChange={(_, value, reason) => {
                        setGlCodeInput(value);

                        if (reason === "clear") {
                            setGlCode("");
                            return;
                        }

                        const normalizedValue = value.trim().toLowerCase();
                        if (normalizedValue === "") {
                            setGlCode("");
                            return;
                        }

                        const exactMatch = glCodeOptions.find((option) => {
                            const glCodeValue = String(option.glCode ?? "").trim().toLowerCase();
                            const label = formatGlCodeOption(option).trim().toLowerCase();
                            return normalizedValue === glCodeValue || normalizedValue === label;
                        });

                        setGlCode(exactMatch?.glCode ?? "");
                    }}
                    onChange={(_, value) => {
                        setGlCode(value?.glCode ?? "");
                        setGlCodeInput(value ? formatGlCodeOption(value) : "");
                    }}
                    renderInput={(params) => (
                        <TextField {...params} label="GL Code" size="small" fullWidth />
                    )}
                />
                <Autocomplete
                    id="trainer-entry-rate-code"
                    options={rateCodeOptions}
                    loading={isPayRatesLoading}
                    value={selectedRateCode}
                    inputValue={rateCodeInput}
                    getOptionLabel={(option) => formatRateCodeOption(option)}
                    isOptionEqualToValue={(option, value) => option.rateCode === value.rateCode}
                    onInputChange={(_, value, reason) => {
                        setRateCodeInput(value);

                        if (reason === "clear") {
                            handleRateCodeChange(null);
                            return;
                        }

                        const normalizedValue = value.trim().toLowerCase();
                        if (normalizedValue === "") {
                            handleRateCodeChange(null);
                            return;
                        }

                        const exactMatch = rateCodeOptions.find((option) => {
                            const rateCodeValue = String(option.rateCode ?? "").trim().toLowerCase();
                            const label = formatRateCodeOption(option).trim().toLowerCase();
                            return normalizedValue === rateCodeValue || normalizedValue === label;
                        });

                        if (!exactMatch) {
                            setRateCode("");
                            return;
                        }

                        handleRateCodeChange(exactMatch);
                    }}
                    onChange={(_, value) => {
                        handleRateCodeChange(value);
                        setRateCodeInput(value ? formatRateCodeOption(value) : "");
                    }}
                    renderInput={(params) => (
                        <TextField {...params} label="Rate Code" size="small" fullWidth />
                    )}
                />
                <TextField
                    id="trainer-entry-units"
                    label="Units"
                    size="small"
                    fullWidth
                    value={units}
                    onChange={(e) => setUnits(e.target.value)}
                />
                <TextField
                    id="trainer-entry-hours"
                    label="Hours"
                    size="small"
                    fullWidth
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                />
                <TextField
                    id="trainer-entry-fee"
                    label="Fee"
                    size="small"
                    fullWidth
                    disabled={lockFeeField}
                    value={fee}
                    onChange={(e) => setFee(e.target.value)}
                />

                <CclDrawerActionBar>
                    <CclButton mode="primary" disabled={!canSave || isSaving || isEditingSaving} onClick={handleSave}>
                        Save
                    </CclButton>
                    <CclButton mode="secondary" onClick={props.onClose}>
                        Close
                    </CclButton>
                </CclDrawerActionBar>
                <CclGenericAccordian headingText={"Rate Codes"} defaultExpanded={false}>
                    <Box sx={{ width: 1, pt: 1 }}>
                        <DataGridPremium
                            rows={rateCodeRows}
                            columns={rateCodeColumns}
                            loading={isPayRatesLoading}
                            autoHeight
                            disableSelectionOnClick
                            disableColumnFilter
                            disableColumnMenu
                            hideFooter
                            density="compact"
                            sortingOrder={["asc", "desc"]}
                        />
                    </Box>
                </CclGenericAccordian>
            </Stack>
        </CclDrawer>
         
    );
};

export default ModifyTrainerEntryDrawer;
